// Budgets editor — the hardest reactive page, proving the fine-grained binding
// primitive on real live-edit forms. Every editable field is a string signal
// bound via bindInput (focus-preserving); derived totals (allocated / scheduled
// income / remaining) and per-category spend status recompute in place via
// computed()+text()+toggleClass, WITHOUT re-rendering the inputs. Row lists are
// re-rendered only on add/remove. All budget math is reused unchanged from the
// shared lib.
import { h, mount, type Child } from '../core/dom';
import { signal, computed, effect, type Signal } from '../core/signal';
import { bindInput, text, toggleClass } from '../core/bind';
import { getCurrentMonth, getPayCycleRange, changeMonth, isValidMonthKey, formatMonth } from '@lib/month';
import { isUncategorised } from '@lib/category';
import { navigate, setNavGuard } from '../core/router';
import {
	computeMonthlyBudget,
	orderBudgetItems,
	buildIncomeBreakdown,
	matchBreakdownActuals,
	formatDate,
	type CategoryBudgetItem,
} from '@lib/budget';
import {
	parseAmount,
	formatAmountOnBlur,
	draftsTotal,
	newScheduleDraft,
	newCategoryLineDraft,
	draftFromSchedule,
	lineDraftFromCategoryLine,
	survivesClone,
	type IncomeScheduleDraft,
	type CategoryLineDraft,
	type Range,
} from '@lib/budgetDraft';
import { buildBudgetPayload } from '@lib/budgetValidation';
import { formatError, type Budget, type Category } from '@lib/db';
import { formatCurrency } from '@lib/format';
import { loadBudgetData, loadBudgetForMonth, saveBudget } from '../data';
import { monthNav } from '../components/monthNav';
import { setHeaderControl } from '../components/headerSlot';
import { loading } from '../ui/loading';
import { modal } from '../ui/modal';
import { breakdownBody } from '../components/incomeBreakdown';

type Sig = Signal<string>;

// A row of signals shared by income schedules and category breakdown lines.
interface RowSig {
	id: string;
	name: Sig;
	category?: Sig; // income schedules only
	amount: Sig;
	frequency: Sig;
	monthlyDay: Sig;
	fortnightlyPayDate: Sig;
	fourWeeklyPayDate: Sig;
	oneOffDate: Sig;
	weeklyDay: Sig;
}

let counter = 0;
const nextId = () => `r${counter++}`;

function rowFrom(d: IncomeScheduleDraft | CategoryLineDraft, withCategory: boolean): RowSig {
	return {
		id: d.id,
		name: signal(d.name),
		category: withCategory ? signal((d as IncomeScheduleDraft).category) : undefined,
		amount: signal(d.amount),
		frequency: signal(d.frequency),
		monthlyDay: signal(d.monthlyDay),
		fortnightlyPayDate: signal(d.fortnightlyPayDate),
		fourWeeklyPayDate: signal(d.fourWeeklyPayDate),
		oneOffDate: signal(d.oneOffDate),
		weeklyDay: signal(d.weeklyDay),
	};
}

function schedDraftOf(r: RowSig): IncomeScheduleDraft {
	return {
		id: r.id,
		name: r.name(),
		category: r.category ? r.category() : '',
		amount: r.amount(),
		frequency: r.frequency() as IncomeScheduleDraft['frequency'],
		monthlyDay: r.monthlyDay(),
		fortnightlyPayDate: r.fortnightlyPayDate(),
		fourWeeklyPayDate: r.fourWeeklyPayDate(),
		oneOffDate: r.oneOffDate(),
		weeklyDay: r.weeklyDay(),
	};
}

function lineDraftOf(r: RowSig): CategoryLineDraft {
	const { category, ...rest } = schedDraftOf(r);
	void category;
	return rest;
}

export function budgetsRoute(query?: URLSearchParams): Node {
	const container = h('div', {});
	const qMonth = query?.get('month');
	const month = isValidMonthKey(qMonth) ? qMonth : getCurrentMonth();
	// Month changes just navigate — the router nav-guard registered by build()
	// owns the unsaved-edit confirm (it also covers leaving via the top nav).
	const goto = (offset: number) => navigate(`/budgets?month=${changeMonth(month, offset)}`);
	renderEditor();
	return container;

	function renderEditor(): void {
		setHeaderControl(monthNav(month, goto));
		const loadingShell = h('div', { class: 'app-shell-inner' }, loading('Loading budget…'));
		container.replaceChildren(loadingShell);
		loadBudgetData(month)
			.then((data) => build(data.categories, data.budget, data.transactions, data.debts))
			.catch((e) => container.replaceChildren(h('div', { class: 'state-error' }, formatError(e as Error))));
	}

	function build(
		categories: Category[],
		budget: Awaited<ReturnType<typeof loadBudgetData>>['budget'],
		transactions: Awaited<ReturnType<typeof loadBudgetData>>['transactions'],
		debts: Awaited<ReturnType<typeof loadBudgetData>>['debts']
	): void {
		const range: Range = getPayCycleRange(month);
		const isNewBudget = !budget;
		const sortedCats = [...categories].sort((a, b) => a.name.localeCompare(b.name));
		const knownNames = sortedCats.map((c) => c.name);

		// --- editable state (one signal per field) ---
		const amount = new Map<string, Sig>();
		const lineRows = new Map<string, Signal<RowSig[]>>();
		const expanded = new Map<string, Signal<boolean>>();
		for (const c of sortedCats) {
			amount.set(c.name, signal(formatAmountOnBlur(budget?.categoryBudgets?.[c.name] ?? '')));
			const savedLines = budget?.categoryLines?.[c.name] ?? [];
			lineRows.set(
				c.name,
				signal(savedLines.map((l) => rowFrom(lineDraftFromCategoryLine(l, nextId(), range), false)))
			);
			expanded.set(c.name, signal(savedLines.length > 0));
		}
		const schedules = signal<RowSig[]>(
			(budget?.incomeSchedules ?? []).map((s) => rowFrom(draftFromSchedule(s, nextId(), range), true))
		);
		const sortBy = signal<'status' | 'name'>('status');
		const showFullySpent = signal(false);
		// Income is defined at month start and rarely touched after — the editor
		// collapses out of the way once schedules exist.
		const incomeOpen = signal((budget?.incomeSchedules ?? []).length === 0);
		const saving = signal(false);
		const error = signal<string | null>(null);
		const saved = signal<string | null>(null);

		// --- derived (read the field signals; recompute on any keystroke) ---
		const amountsRecord = (): Record<string, string> => {
			const r: Record<string, string> = {};
			for (const [k, s] of amount) r[k] = s();
			return r;
		};
		const lineDraftsRecord = (): Record<string, CategoryLineDraft[]> => {
			const r: Record<string, CategoryLineDraft[]> = {};
			for (const [k, rows] of lineRows) r[k] = rows().map(lineDraftOf);
			return r;
		};
		const schedDrafts = (): IncomeScheduleDraft[] => schedules().map(schedDraftOf);
		const hasRealLines = (cat: string) =>
			lineRows.get(cat)!().some((r) => r.amount().trim() !== '' || r.name().trim() !== '');
		const lineTotalOf = (cat: string) => draftsTotal(lineRows.get(cat)!().map(lineDraftOf), month);

		const incomeCatNames = computed(() => new Set(schedDrafts().map((d) => d.category.trim()).filter(Boolean)));
		const liveBudgets = computed<Record<string, number>>(() => {
			const r: Record<string, number> = {};
			for (const name of knownNames) {
				if (hasRealLines(name)) r[name] = lineTotalOf(name);
				else {
					const p = parseAmount(amount.get(name)!());
					if (p !== null) r[name] = p;
				}
			}
			return r;
		});
		const items = computed<CategoryBudgetItem[]>(() =>
			computeMonthlyBudget({
				transactions,
				debts,
				categoryBudgets: liveBudgets(),
				isNewBudget,
				incomeCategoryNames: incomeCatNames(),
				knownCategoryNames: knownNames,
				mode: 'all-non-income',
			})
		);
		const itemsByName = computed(() => new Map(items().map((i) => [i.name, i])));
		const allocated = computed(() => {
			const inc = incomeCatNames();
			return Object.entries(liveBudgets()).reduce((s, [n, v]) => (inc.has(n) ? s : s + v), 0);
		});
		const scheduledIncome = computed(() => draftsTotal(schedDrafts(), month));
		const remaining = computed(() => scheduledIncome() - allocated());

		// Frozen display order — recomputed only on load/sort/save so rows don't
		// reorder while typing.
		const layout = signal(orderBudgetItems(items(), sortBy()));
		const relayout = () => layout.set(orderBudgetItems(items(), sortBy()));

		// --- clone-previous-budget (offered only on a blank new budget) ---
		type CloneState =
			| { status: 'idle' }
			| { status: 'loading' }
			| { status: 'none' }
			| { status: 'error'; message: string }
			| { status: 'found'; month: string; budget: Budget };
		const isBlankBudget = isNewBudget && !transactions.some((t) => !isUncategorised(t.category));
		const clone = signal<CloneState>({ status: 'idle' });
		if (isBlankBudget) {
			clone.set({ status: 'loading' });
			void (async () => {
				try {
					// Walk back up to 12 months for the most recent saved budget.
					let cursor = month;
					for (let i = 0; i < 12; i++) {
						cursor = changeMonth(cursor, -1);
						const candidate = await loadBudgetForMonth(cursor);
						if (!container.isConnected) return;
						if (candidate) {
							clone.set({ status: 'found', month: cursor, budget: candidate });
							return;
						}
					}
					clone.set({ status: 'none' });
				} catch (e) {
					if (container.isConnected) clone.set({ status: 'error', message: formatError(e as Error) });
				}
			})();
		}

		function applyClone(c: { month: string; budget: Budget }): void {
			for (const [name, value] of Object.entries(c.budget.categoryBudgets ?? {})) {
				amount.get(name)?.set(formatAmountOnBlur(String(value)));
			}
			// One-off lines/schedules belong to the source cycle — drop any whose
			// date falls outside this month's cycle instead of cloning a line the
			// save-time validation would reject.
			for (const [name, lines] of Object.entries(c.budget.categoryLines ?? {})) {
				const kept = lines.filter((l) => survivesClone(l, range));
				if (kept.length === 0) continue;
				lineRows.get(name)?.set(kept.map((l) => rowFrom(lineDraftFromCategoryLine(l, nextId(), range), false)));
				expanded.get(name)?.set(true);
			}
			schedules.set(
				(c.budget.incomeSchedules ?? [])
					.filter((s) => survivesClone(s, range))
					.map((s) => rowFrom(draftFromSchedule(s, nextId(), range), true))
			);
			clone.set({ status: 'idle' }); // applied — hide the offer
			relayout();
		}

		// --- save / change detection ---
		const snapshot = () =>
			JSON.stringify(buildBudgetPayload(amountsRecord(), lineDraftsRecord(), schedDrafts(), month).payload);
		let lastSaved = snapshot();
		const hasUnsaved = () => snapshot() !== lastSaved;
		// Guard ALL in-app navigation away from unsaved edits (month switcher,
		// top nav, dashboard links). The router clears it on allowed navigation.
		setNavGuard(() => !hasUnsaved() || confirm('Discard unsaved budget changes?'));

		async function save(): Promise<void> {
			const built = buildBudgetPayload(amountsRecord(), lineDraftsRecord(), schedDrafts(), month);
			if (built.error) {
				error.set(built.error);
				return;
			}
			error.set(null);
			saving.set(true);
			try {
				await saveBudget(month, built.payload ?? {});
				lastSaved = snapshot();
				saved.set('Saved');
				relayout();
				setTimeout(() => saved.set(null), 2500);
			} catch (e) {
				error.set(formatError(e as Error));
			} finally {
				saving.set(false);
			}
		}

		// beforeunload guard while this editor is mounted; self-removes once the
		// route's DOM is unmounted so listeners don't stack across navigations.
		const beforeUnload = (e: BeforeUnloadEvent) => {
			if (!container.isConnected) {
				window.removeEventListener('beforeunload', beforeUnload);
				return;
			}
			if (hasUnsaved()) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		window.addEventListener('beforeunload', beforeUnload);

		// --- render ---
		container.replaceChildren(
			h(
				'div',
				{ class: 'app-shell-inner' },
				cloneCard(),
				summaryCard(),
				categorySection(),
				saveBar()
			)
		);

		function cloneCard(): Child {
			const box = h('div', {});
			mount(box, () => {
				const c = clone();
				if (c.status === 'idle') return null;
				return h(
					'div',
					{ class: 'clone-card' },
					h('h2', {}, 'Start from a previous budget'),
					c.status === 'loading'
						? h('p', { class: 'muted' }, 'Looking for your most recent budget…')
						: c.status === 'error'
							? h('p', { class: 'state-error' }, c.message)
							: c.status === 'none'
								? h('p', { class: 'muted' }, 'No previous budgets found to clone.')
								: [
										h('p', { class: 'muted' }, `Clone ${formatMonth(c.month)} to pre-fill income and category budgets, then review and adjust before saving.`),
										h('div', { class: 'row' }, h('button', { class: 'btn btn-secondary', type: 'button', onclick: () => applyClone(c) }, `Clone ${formatMonth(c.month)}`)),
									]
				);
			});
			return box;
		}

		function summaryCard(): Child {
			const editBtn = h(
				'button',
				{ class: 'btn btn-sm btn-secondary', onclick: () => incomeOpen.update((v) => !v) },
				text(() => (incomeOpen() ? 'Hide income ▾' : `Edit income (${schedules().length}) ▸`))
			);
			effect(() => editBtn.setAttribute('aria-expanded', String(incomeOpen())));
			const editorBox = h('div', {});
			mount(editorBox, () => (incomeOpen() ? incomeEditor() : null));
			return h(
				'div',
				{ class: 'section' },
				h(
					'div',
					{ class: 'summary-card' },
					summaryItem('Scheduled income', () => formatCurrency(scheduledIncome())),
					summaryItem('Allocated', () => formatCurrency(allocated())),
					remainingItem(),
					h('button', { class: 'btn btn-sm btn-secondary spacer', onclick: openBreakdownModal }, 'Income breakdown'),
					editBtn
				),
				editorBox
			);
		}

		function summaryItem(label: string, value: () => string): Child {
			return h('div', { class: 'summary-item' }, h('span', { class: 'label' }, label), h('span', { class: 'value' }, text(value)));
		}
		function remainingItem(): Child {
			const val = h('span', { class: 'value' }, text(() => formatCurrency(remaining())));
			toggleClass(val, 'negative', () => remaining() < 0);
			return h('div', { class: 'summary-item' }, h('span', { class: 'label' }, 'Remaining'), val);
		}

		function openBreakdownModal(): void {
			const rows = buildIncomeBreakdown(
				schedDrafts()
					.filter((d) => d.category.trim() && (parseAmount(d.amount) ?? 0) !== 0)
					.map((d) => ({
						name: d.name.trim() || undefined,
						category: d.category.trim(),
						amount: parseAmount(d.amount) ?? 0,
						frequency: d.frequency,
						monthlyDay: d.frequency === 'monthly' ? Number(d.monthlyDay) || 15 : undefined,
						fortnightlyPayDate: d.frequency === 'fortnightly' ? d.fortnightlyPayDate : undefined,
						fourWeeklyPayDate: d.frequency === 'four-weekly' ? d.fourWeeklyPayDate : undefined,
						oneOffDate: d.frequency === 'one-off' ? d.oneOffDate : undefined,
						weeklyDay: d.frequency === 'weekly' ? Number(d.weeklyDay) : undefined,
					})),
				month
			);
			modal({ title: 'Income Schedule', body: breakdownBody(matchBreakdownActuals(rows, transactions)) });
		}

		// --- income schedules (collapsible editor under the summary card) ---
		function incomeEditor(): Child {
			const list = h('div', { class: 'schedule-editor' });
			mount(list, () =>
				schedules().length === 0
					? h('p', { class: 'muted' }, 'Add pay schedules to track expected income to date.')
					: schedules().map((r) => scheduleRow(r))
			);
			return h(
				'div',
				{ class: 'income-editor' },
				list,
				h(
					'button',
					{
						class: 'btn btn-sm btn-secondary',
						onclick: () => schedules.update((rows) => [...rows, rowFrom(newScheduleDraft(nextId(), range), true)]),
					},
					'+ Add income'
				)
			);
		}

		function scheduleRow(r: RowSig): Child {
			const nameI = h('input', { class: 'input', placeholder: 'Name (optional)', 'aria-label': 'Income name' }) as HTMLInputElement;
			bindInput(nameI, r.name);
			const catI = h('input', { class: 'input', placeholder: 'Category', list: 'cat-options', 'aria-label': 'Income category' }) as HTMLInputElement;
			bindInput(catI, r.category!);
			return h(
				'div',
				{ class: 'income-row' },
				h('div', { class: 'income-fields' }, nameI, catI),
				h(
					'div',
					{ class: 'income-schedule' },
					dollarInput(r.amount),
					freqControls(r),
					removeBtn(() => schedules.update((rows) => rows.filter((x) => x.id !== r.id)))
				)
			);
		}

		// --- categories ---
		function categorySection(): Child {
			const wrap = h('div', { class: 'section' });
			const head = h(
				'div',
				{ class: 'section-head' },
				h('h2', {}, 'Category budgets'),
				h(
					'button',
					{
						class: 'btn btn-sm btn-secondary',
						disabled: isNewBudget || undefined,
						onclick: () => {
							sortBy.set(sortBy() === 'status' ? 'name' : 'status');
							relayout();
							renderCats();
						},
					},
					text(() => `Sort: ${sortBy() === 'status' ? 'Status' : 'Name'}`)
				)
			);
			const activeWrap = h('div', { class: 'row-stack' });
			const fsWrap = h('div', {});
			wrap.append(head, activeWrap, fsWrap);
			renderCats();
			return wrap;

			function renderCats(): void {
				const { active, fullySpent } = layout();
				activeWrap.replaceChildren(...active.map((i) => catRow(i.name)));
				fsWrap.replaceChildren(
					fullySpent.length === 0
						? document.createComment('')
						: h(
								'div',
								{ class: 'row-stack' },
								(() => {
									const btn = h(
										'button',
										{ class: 'btn btn-link', onclick: () => showFullySpent.update((v) => !v) },
										text(() => `${showFullySpent() ? 'Hide' : 'Show'} fully spent (${fullySpent.length})`)
									);
									effect(() => btn.setAttribute('aria-expanded', String(showFullySpent())));
									return btn;
								})(),
								(() => {
									const box = h('div', { class: 'row-stack' });
									mount(box, () => (showFullySpent() ? fullySpent.map((i) => catRow(i.name)) : null));
									return box;
								})()
							)
				);
			}
		}

		function catRow(name: string): HTMLElement {
			const amountSig = amount.get(name)!;
			const spent = h('span', { class: 'cat-spent' }, text(() => {
				const it = itemsByName().get(name);
				return it ? `${formatCurrency(it.spent)} spent` : '';
			}));
			toggleClass(spent, 'over', () => !!itemsByName().get(name)?.isOverBudget);
			toggleClass(spent, 'warning', () => {
				const it = itemsByName().get(name);
				return !!it && !it.isOverBudget && it.percentUsed > 80;
			});

			// Amount input: editable when no breakdown lines; read-only (shows the
			// line total) when lines exist.
			const amtInput = h('input', { class: 'input', inputmode: 'decimal', 'aria-label': `${name} budget amount` }) as HTMLInputElement;
			const dollar = h('div', { class: 'dollar-input' }, h('span', { class: 'prefix' }, '$'), amtInput);
			bindInput(amtInput, amountSig);
			amtInput.addEventListener('blur', () => amountSig.set(formatAmountOnBlur(amountSig())));
			// Single source of truth: when breakdown lines exist the amount is the
			// line total (read-only, back-filled into the signal so bindInput shows
			// it); otherwise it's freely editable.
			effect(() => {
				if (hasRealLines(name)) {
					amtInput.readOnly = true;
					amountSig.set(formatAmountOnBlur(lineTotalOf(name)));
				} else {
					amtInput.readOnly = false;
				}
			});

			const linesSig = lineRows.get(name)!;
			const exp = expanded.get(name)!;
			const lineBox = h('div', {});
			mount(lineBox, () => (exp() ? lineEditor(name, linesSig, range) : null));

			const row = h(
				'div',
				{ class: 'cat-row' },
				h(
					'div',
					{ class: 'cat-row-main' },
					h('span', { class: 'cat-name' }, name),
					spent,
					dollar,
					(() => {
						const btn = h('button', { class: 'btn btn-sm btn-secondary', onclick: () => exp.update((v) => !v) }, text(() => (exp() ? 'Breakdown ▾' : 'Breakdown ▸')));
						effect(() => btn.setAttribute('aria-expanded', String(exp())));
						return btn;
					})()
				),
				lineBox
			);
			toggleClass(row, 'over', () => !!itemsByName().get(name)?.isOverBudget);
			toggleClass(row, 'under', () => !!itemsByName().get(name)?.isUnderBudget);
			toggleClass(row, 'unbudgeted', () => !!itemsByName().get(name)?.isUnbudgeted);
			return row;
		}

		function lineEditor(cat: string, linesSig: Signal<RowSig[]>, r: Range): Child {
			const list = h('div', { class: 'line-editor' });
			mount(list, () => linesSig().map((row) => lineRow(cat, row, linesSig)));
			return h(
				'div',
				{},
				list,
				h(
					'button',
					{ class: 'btn btn-sm btn-secondary', onclick: () => linesSig.update((rows) => [...rows, rowFrom(newCategoryLineDraft(nextId(), r), false)]) },
					'+ Add line'
				)
			);
		}

		function lineRow(cat: string, row: RowSig, linesSig: Signal<RowSig[]>): Child {
			void cat;
			const nameI = h('input', { class: 'input', placeholder: 'Label (optional)', 'aria-label': 'Line label' }) as HTMLInputElement;
			bindInput(nameI, row.name);
			return h(
				'div',
				{ class: 'income-row' },
				h('div', { class: 'income-fields' }, nameI),
				h(
					'div',
					{ class: 'income-schedule' },
					dollarInput(row.amount),
					freqControls(row),
					removeBtn(() => linesSig.update((rows) => rows.filter((x) => x.id !== row.id)))
				)
			);
		}

		// --- shared field controls ---
		function dollarInput(sig: Sig): Child {
			const input = h('input', { class: 'input', inputmode: 'decimal', placeholder: '0.00', 'aria-label': 'Amount' }) as HTMLInputElement;
			bindInput(input, sig);
			input.addEventListener('blur', () => sig.set(formatAmountOnBlur(sig())));
			return h('div', { class: 'dollar-input' }, h('span', { class: 'prefix' }, '$'), input);
		}

		function freqControls(r: RowSig): Child[] {
			const select = h('select', { class: 'select freq-select', 'aria-label': 'Frequency' }) as HTMLSelectElement;
			for (const f of ['monthly', 'fortnightly', 'four-weekly', 'weekly', 'one-off']) select.append(h('option', { value: f }, f));
			bindInput(select, r.frequency, 'change');
			const fieldBox = h('div', { class: 'freq-field' });
			mount(fieldBox, () => freqField(r));
			return [select, fieldBox];
		}

		function freqField(r: RowSig): Child {
			const f = r.frequency();
			if (f === 'monthly') {
				const i = h('input', { class: 'input', type: 'number', min: '1', max: '31', placeholder: 'Day of month', 'aria-label': 'Day of month' }) as HTMLInputElement;
				bindInput(i, r.monthlyDay);
				return i;
			}
			if (f === 'weekly') {
				const s = h('select', { class: 'select', 'aria-label': 'Day of week' }) as HTMLSelectElement;
				// Monday-first display (values stay 0=Sun..6=Sat), matching Svelte.
				for (const [v, d] of [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun']]) {
					s.append(h('option', { value: v }, d));
				}
				bindInput(s, r.weeklyDay, 'change');
				return s;
			}
			const dateSig = f === 'fortnightly'
				? r.fortnightlyPayDate
				: f === 'four-weekly'
					? r.fourWeeklyPayDate
					: r.oneOffDate;
			const d = h('input', { class: 'input', type: 'date', min: formatDate(range.start), max: formatDate(range.end), 'aria-label': 'Date' }) as HTMLInputElement;
			bindInput(d, dateSig, 'change');
			return d;
		}

		function removeBtn(onClick: () => void): Child {
			return h('button', { class: 'x-btn', type: 'button', 'aria-label': 'Remove', onclick: onClick }, '×');
		}

		function saveBar(): Child {
			const err = h('span', { class: 'state-error' }, text(() => error() ?? ''));
			toggleClass(err, 'hidden', () => !error());
			const btn = h('button', { class: 'btn btn-primary', onclick: () => void save() }, text(() => (saving() ? 'Saving…' : 'Save budget')));
			return h(
				'div',
				{ class: 'save-bar' },
				btn,
				(() => {
					// Tinted pill: hide it entirely when there's no message (an empty
					// span still renders its background/border).
					const ok = h('span', { class: 'state-success' }, text(() => saved() ?? ''));
					toggleClass(ok, 'hidden', () => !saved());
					return ok;
				})(),
				err,
				// hidden datalist of categories for income schedule category inputs
				(() => {
					const dl = h('datalist', { id: 'cat-options' });
					for (const c of sortedCats) dl.append(h('option', { value: c.name }));
					return dl;
				})()
			);
		}
	}
}
